import type {
  Invoice,
  PaymentHistoryEntry,
  PaymentStatusApiRecord,
  PaymentStatusRecord,
  PaymentTrackingStatus,
  ReceivedPaymentItem,
  UpdatePaymentStatusPayload,
} from "./types";
import { BILLING_FREQUENCY_OPTIONS } from "./billingPeriodUtils";
import { extractPaginatedList } from "./billingPaginationUtils";
import { formatInvoiceListDate } from "./invoiceListUtils";

export const PAYMENT_STATUS_QUERY_KEY = ["payment-status"] as const;

export function formatDisplayDate(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function parseDisplayDateToInput(value?: string | null): string {
  if (!value?.trim()) {
    return "";
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  const parts = value.trim().split(/\s+/);
  if (parts.length === 3) {
    const [day, monthLabel, year] = parts;
    const monthIndex = new Date(`${monthLabel} 1, 2000`).getMonth();
    if (!Number.isNaN(monthIndex)) {
      const date = new Date(Number(year), monthIndex, Number(day));
      if (!Number.isNaN(date.getTime())) {
        return date.toISOString().slice(0, 10);
      }
    }
  }

  return "";
}

export function getInvoiceReceivedItems(
  invoice: Invoice
): ReceivedPaymentItem[] {
  if (invoice.receivedItems?.length) {
    return invoice.receivedItems;
  }

  if (invoice.received && invoice.received > 0) {
    return [
      {
        id: `${invoice.id}-received-1`,
        amount: invoice.received,
        date:
          parseDisplayDateToInput(invoice.receivedDate) ||
          (invoice.receivedDate ?? ""),
      },
    ];
  }

  return [];
}

export function getTotalReceivedAmount(items: ReceivedPaymentItem[]): number {
  return items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
}

export function getLatestReceivedDate(
  items: ReceivedPaymentItem[]
): string | null {
  if (items.length === 0) {
    return null;
  }

  const sorted = [...items].sort((a, b) => {
    const aTime = new Date(`${a.date}T00:00:00`).getTime();
    const bTime = new Date(`${b.date}T00:00:00`).getTime();
    return bTime - aTime;
  });

  const latest = sorted[0]?.date;
  return latest ? formatDisplayDate(latest) : null;
}

export function applyReceivedItemsToInvoice(
  invoice: Invoice,
  receivedItems: ReceivedPaymentItem[],
  status: Invoice["status"]
): Invoice {
  const received = getTotalReceivedAmount(receivedItems);
  const pending = Math.max(invoice.total - received, 0);
  const receivedDate = getLatestReceivedDate(receivedItems);
  const isPaid = status === "Paid" && pending === 0;

  return {
    ...invoice,
    status,
    receivedItems,
    received,
    receivedDate,
    pending,
    paidDate: isPaid ? receivedDate : invoice.paidDate,
    paid: isPaid ? receivedDate : invoice.paid,
  };
}

function parseAmount(value?: string | number): number {
  if (value === undefined || value === null || value === "") {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function resolveFrequencyLabel(frequency?: string | null): string {
  if (!frequency?.trim()) {
    return "—";
  }

  const normalized = frequency.trim().toLowerCase();
  const match = BILLING_FREQUENCY_OPTIONS.find(
    option =>
      option.value === normalized ||
      option.label.trim().toLowerCase() === normalized
  );

  return match?.label ?? frequency.trim();
}

function normalizePaymentTrackingStatus(
  status?: string,
  receivedAmount = 0,
  total = 0
): PaymentTrackingStatus {
  const value = status?.trim().toLowerCase() ?? "";

  if (value === "paid") {
    return "Paid";
  }

  if (value === "overdue") {
    return "Overdue";
  }

  if (value === "pending" || value === "partial") {
    return "Pending";
  }

  if (total > 0 && receivedAmount >= total) {
    return "Paid";
  }

  return "Pending";
}

function formatPaymentBillingPeriod(from?: string, to?: string): string {
  const fromValue = from?.trim();
  const toValue = to?.trim();

  if (fromValue && toValue) {
    return `${formatInvoiceListDate(fromValue)} – ${formatInvoiceListDate(toValue)}`;
  }

  if (fromValue) {
    return formatInvoiceListDate(fromValue);
  }

  if (toValue) {
    return formatInvoiceListDate(toValue);
  }

  return "—";
}

function mapPaymentHistoryEntries(
  history: PaymentStatusApiRecord["payment_history"],
  recordId: string
): PaymentHistoryEntry[] {
  if (!Array.isArray(history)) {
    return [];
  }

  return history.map((item, index) => ({
    id: String(item.id ?? `${recordId}-payment-${index + 1}`),
    receivedAmount: parseAmount(item.received_amount),
    receivedDate: item.received_date?.trim() ?? "",
    pendingAmount: parseAmount(item.pending_amount),
    remarks: item.remarks?.trim() ?? "",
  }));
}

function mapPaymentHistoryToReceivedItems(
  paymentHistory: PaymentHistoryEntry[]
): ReceivedPaymentItem[] {
  return paymentHistory.map(item => ({
    id: item.id,
    amount: item.receivedAmount,
    date: item.receivedDate,
  }));
}

function buildPaymentHistoryFromReceivedItems(
  receivedItems: ReceivedPaymentItem[],
  total: number
): PaymentHistoryEntry[] {
  let runningReceived = 0;

  return receivedItems.map((item, index) => {
    runningReceived += item.amount;

    return {
      id: item.id || `payment-${index + 1}`,
      receivedAmount: item.amount,
      receivedDate: item.date,
      pendingAmount: Math.max(total - runningReceived, 0),
      remarks: "",
    };
  });
}

export function normalizePaymentStatusRecord(
  record: PaymentStatusApiRecord
): PaymentStatusRecord | null {
  const recordId = record.id?.trim();
  if (!recordId) {
    return null;
  }

  const total = parseAmount(record.grand_total);
  const received = parseAmount(record.received_amount);
  const pending = parseAmount(record.pending_amount);
  const paymentHistory = mapPaymentHistoryEntries(
    record.payment_history,
    recordId
  );
  const receivedItems = mapPaymentHistoryToReceivedItems(paymentHistory);
  const receivedDate = getLatestReceivedDate(receivedItems);
  const invoiceNumber = record.invoice_number?.trim();

  return {
    recordId,
    id: invoiceNumber ?? recordId,
    client: record.client_name?.trim() ?? "—",
    project: record.project?.trim() ?? undefined,
    frequency: resolveFrequencyLabel(record.frequency),
    period: formatPaymentBillingPeriod(
      record.billing_period_from,
      record.billing_period_to
    ),
    amount: parseAmount(record.subtotal),
    gst: parseAmount(record.tax_total),
    total,
    status: normalizePaymentTrackingStatus(record.status, received, total),
    invoiceDate: record.invoice_date?.trim()
      ? formatDisplayDate(record.invoice_date.trim())
      : null,
    received,
    receivedDate,
    pending,
    nextDate: null,
    receivedItems,
    paymentHistory,
  };
}

export function extractPaymentStatusRecords(
  response: unknown
): PaymentStatusApiRecord[] {
  return extractPaginatedList(response).filter(
    (item): item is PaymentStatusApiRecord =>
      Boolean(item) && typeof item === "object"
  );
}

export function normalizePaymentStatusList(
  response: unknown
): PaymentStatusRecord[] {
  return extractPaymentStatusRecords(response)
    .map(normalizePaymentStatusRecord)
    .filter((record): record is PaymentStatusRecord => record !== null);
}

export function getPaymentStatusReceivedItems(
  record: PaymentStatusRecord
): ReceivedPaymentItem[] {
  if (record.receivedItems?.length) {
    return record.receivedItems;
  }

  if (record.received && record.received > 0) {
    return [
      {
        id: `${record.recordId}-received-1`,
        amount: record.received,
        date:
          parseDisplayDateToInput(record.receivedDate) ||
          (record.receivedDate ?? ""),
      },
    ];
  }

  return [];
}

export function applyReceivedItemsToPaymentStatus(
  record: PaymentStatusRecord,
  receivedItems: ReceivedPaymentItem[],
  status: PaymentTrackingStatus
): PaymentStatusRecord {
  const received = getTotalReceivedAmount(receivedItems);
  const pending = Math.max(record.total - received, 0);
  const receivedDate = getLatestReceivedDate(receivedItems);
  const paymentHistory = buildPaymentHistoryFromReceivedItems(
    receivedItems,
    record.total
  );

  return {
    ...record,
    status,
    receivedItems,
    paymentHistory,
    received,
    receivedDate,
    pending,
  };
}

export function toApiPaymentStatus(status: PaymentTrackingStatus): string {
  return status.toLowerCase();
}

export function buildPaymentStatusOnlyPayload(
  status: PaymentTrackingStatus
): UpdatePaymentStatusPayload {
  return {
    status: toApiPaymentStatus(status),
  };
}

export function buildUpdatePaymentStatusPayload(
  status: PaymentTrackingStatus,
  receivedItems: ReceivedPaymentItem[]
): UpdatePaymentStatusPayload {
  return {
    status: toApiPaymentStatus(status),
    payment_history: receivedItems.map(item => ({
      received_amount: item.amount,
      received_date: item.date,
    })),
  };
}
